import React, { FC } from 'react';
import { stringFormat, T } from 'src/shared/helpers';
import styled from 'styled-components';

const Pill = styled.span<{ $assigned: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 500;
    color: ${(props) => (props.$assigned ? '#166534' : '#78716c')};
    background: ${(props) => (props.$assigned ? '#dcfce7' : '#f3f4f6')};
    border-radius: 12px;
    padding: 2px 8px;
    width: fit-content;
    white-space: nowrap;
`;

export type AssignmentDimension = 'products' | 'printingMethods' | 'colors' | 'globalColors' | 'variants';

interface DimensionLabels {
    singular: string;
    plural: string;
}

const getDimensionLabels = (dimension: AssignmentDimension): DimensionLabels => {
    switch (dimension) {
        case 'products':
            return { singular: T._('{0} product', 'Admin'), plural: T._('{0} products', 'Admin') };
        case 'printingMethods':
            return { singular: T._('{0} printing method', 'Admin'), plural: T._('{0} printing methods', 'Admin') };
        case 'colors':
            return { singular: T._('{0} color', 'Admin'), plural: T._('{0} colors', 'Admin') };
        case 'globalColors':
            return { singular: T._('{0} global color', 'Admin'), plural: T._('{0} global colors', 'Admin') };
        case 'variants':
            return { singular: T._('{0} variant', 'Admin'), plural: T._('{0} variants', 'Admin') };
    }
};

export interface AssignmentBadgeProps {
    /**
     * Ordered list of assignment dimensions to consider. The first dimension with a positive
     * count is rendered. If none has any count, the badge falls back to "Unassigned".
     */
    counts: Array<{ dimension: AssignmentDimension; count: number }>;
    className?: string;
}

/**
 * Pill badge that summarizes how many products/printing methods/colors/etc are assigned to a
 * rule. Renders the first non-zero dimension as a green "X products" / "Y printing methods"
 * style label, or a yellow "Unassigned" badge when nothing is assigned.
 */
export const AssignmentBadge: FC<AssignmentBadgeProps> = ({ counts, className }) => {
    const active = counts.find((c) => c.count > 0);
    if (!active) {
        return (
            <Pill $assigned={false} className={className}>
                {T._('Unassigned', 'Admin')}
            </Pill>
        );
    }

    const labels = getDimensionLabels(active.dimension);
    const template = active.count === 1 ? labels.singular : labels.plural;
    return (
        <Pill $assigned={true} className={className}>
            {stringFormat(template, active.count.toString())}
        </Pill>
    );
};
